// 知识库左栏：最近收进 + 知识库里真实的分区（文件夹）。展开一个分区只列最近几篇的标题，多的「查看全部」在中间列出来。
// 篇数用浅色小字，不显示日期：左栏只负责「在哪、找哪篇」，看资料去中间。

import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Network, Search, Settings2, Upload, X } from 'lucide-react'
import { getFolderTree, listKnowledge } from '../../api/kb'
import type { FolderNode, Knowledge, KnowledgeBase } from '../../api/types'
import { IconButton } from '../../ui'
import { docTitle } from './DocCard'

const SHOWN = 6

// 分区的顺序（2026-10-12 重新分区后）：自己的在前，主题按篇数排在中间，收集、素材在后，思考讨论和写作存档的放最后。
// 不在这两个名单里的分区都按篇数排在中间。
const FIRST = ['我写的', '项目资料']
const LAST = ['文章收集', '素材', '思考', '英文日报']
export function orderFolders(folders: FolderNode[]): FolderNode[] {
  const rank = (f: FolderNode) => (FIRST.includes(f.name) ? FIRST.indexOf(f.name) - 100 : LAST.includes(f.name) ? 100 + LAST.indexOf(f.name) : 0)
  return [...folders].sort((a, b) => rank(a) - rank(b) || b.total_count - a.total_count || a.name.localeCompare(b.name, 'zh-CN'))
}

/** 文件夹：undefined = 全部；'' = 根目录；其余 = 这个文件夹。 */
export type FolderSel = string | undefined

export function KbTree({ kbId, kb, doc, folder, keyword, onUpload, onSettings, onClose }: { kbId: string; kb?: KnowledgeBase; doc?: Knowledge; folder: FolderSel; keyword: string; onUpload: () => void; onSettings: () => void; onClose?: () => void }) {
  const navigate = useNavigate()
  const tree = useQuery({ queryKey: ['folders', kbId], queryFn: () => getFolderTree(kbId) })
  const recent = useQuery({ queryKey: ['knowledge', kbId, 'recent'], queryFn: () => listKnowledge(kbId, { pageSize: SHOWN }) })
  const [q, setQ] = useState(keyword)
  useEffect(() => setQ(keyword), [keyword])

  const search = (e: FormEvent) => {
    e.preventDefault()
    const k = q.trim()
    navigate(k ? `/kb/${kbId}?q=${encodeURIComponent(k)}` : `/kb/${kbId}`)
  }

  return (
    <nav className="kbt" aria-label="知识库分区">
      <div className="kbt-head">
        <div className="kbt-title">
          <h1>{kb?.name ?? ''}</h1>
          {tree.data && <span className="n">{tree.data.total_document_count}</span>}
          <span className="grow" />
          <IconButton label="上传资料" onClick={onUpload}>
            <Upload />
          </IconButton>
          {kb?.indexing_strategy?.wiki_enabled && (
            <Link className="sk-icon-btn" to={`/kb/${kbId}/wiki`} aria-label="Wiki" title="Wiki">
              <Network />
            </Link>
          )}
          {kb && (
            <IconButton label="知识库设置" onClick={onSettings}>
              <Settings2 />
            </IconButton>
          )}
          {onClose && (
            <IconButton label="收起分区" className="sheet-x" onClick={onClose}>
              <X />
            </IconButton>
          )}
        </div>
        <form className="kbt-search" role="search" onSubmit={search}>
          <Search width={15} height={15} aria-hidden="true" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜索资料" aria-label="搜索资料" />
        </form>
      </div>
      <div className="kbt-body">
        <Section
          name="最近收进"
          pinned
          defaultOpen
          docs={recent.data?.data}
          currentDoc={doc?.id}
          kbId={kbId}
          onAll={() => navigate(`/kb/${kbId}`)}
          allLabel="查看全部"
          current={folder === undefined && !doc && !keyword}
        />
        {!!tree.data?.root_document_count && <FolderSection kbId={kbId} node={{ name: '根目录', path: '', total_count: tree.data.root_document_count, children: [] } as unknown as FolderNode} depth={0} doc={doc} folder={folder} root />}
        {orderFolders(tree.data?.folders ?? []).map((f) => <FolderSection key={f.path} kbId={kbId} node={f} depth={0} doc={doc} folder={folder} />)}
        {tree.isError && <p className="kbt-note">没能载入分区</p>}
      </div>
    </nav>
  )
}

function Section({ name, count, pinned, defaultOpen, docs, currentDoc, kbId, onAll, allLabel, current, depth = 0, children, open: openProp, onToggle, loading }: {
  name: string
  count?: number
  pinned?: boolean
  defaultOpen?: boolean
  docs?: Knowledge[]
  currentDoc?: string
  kbId: string
  onAll: () => void
  allLabel: string
  current?: boolean
  depth?: number
  children?: React.ReactNode
  open?: boolean
  onToggle?: () => void
  loading?: boolean
}) {
  const [ownOpen, setOwnOpen] = useState(!!defaultOpen)
  const open = openProp ?? ownOpen
  const toggle = onToggle ?? (() => setOwnOpen((v) => !v))
  return (
    <div className="kbt-sec" data-open={open} data-pinned={pinned} data-current={current}>
      <button type="button" className="kbt-sec-h" aria-expanded={open} onClick={toggle} style={{ paddingLeft: 8 + depth * 14 }}>
        <ChevronRight className="chev" aria-hidden="true" />
        <span className="nm">{name}</span>
        {count !== undefined && <span className="ct">{count}</span>}
      </button>
      {open && (
        <div className="kbt-docs" aria-busy={loading}>
          {children}
          {docs?.map((d) => (
            <Link key={d.id} to={`/kb/${kbId}/d/${d.id}`} className="kbt-doc" aria-current={currentDoc === d.id ? 'page' : undefined} title={docTitle(d)} style={{ paddingLeft: 30 + depth * 14 }}>
              {docTitle(d)}
            </Link>
          ))}
          {docs && (count === undefined || count > docs.length || pinned) && (
            <button type="button" className="kbt-doc kbt-all" onClick={onAll} style={{ paddingLeft: 30 + depth * 14 }}>
              {count !== undefined && !pinned ? `${allLabel} ${count} 篇` : allLabel}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** 一个分区：展开时才去取这一区最近的几篇；里面的子分区也在这里展开。正在看的资料所在的分区自动展开。 */
function FolderSection({ kbId, node, depth, doc, folder, root }: { kbId: string; node: FolderNode; depth: number; doc?: Knowledge; folder: FolderSel; root?: boolean }) {
  const navigate = useNavigate()
  const inside = (p?: string) => p !== undefined && (p === node.path || (!!node.path && p.startsWith(`${node.path}/`)))
  const docFolder = doc ? doc.folder_path ?? '' : undefined
  const [open, setOpen] = useState(() => inside(docFolder) || inside(folder))
  useEffect(() => {
    if (inside(docFolder) || inside(folder)) setOpen(true)
  }, [docFolder, folder])
  const docs = useQuery({
    queryKey: ['knowledge', kbId, 'section', node.path],
    enabled: open,
    queryFn: () => listKnowledge(kbId, { folderPath: node.path, recursive: false, pageSize: SHOWN }),
  })
  const direct = docs.data?.data ?? []
  return (
    <Section
      name={node.name}
      count={node.total_count}
      kbId={kbId}
      depth={depth}
      open={open}
      onToggle={() => setOpen((v) => !v)}
      docs={docs.data ? direct : undefined}
      loading={docs.isPending && open}
      currentDoc={doc?.id}
      current={folder === node.path && !doc}
      allLabel="查看全部"
      onAll={() => navigate(`/kb/${kbId}?folder=${encodeURIComponent(node.path)}`)}
    >
      {!root && node.children?.map((c) => <FolderSection key={c.path} kbId={kbId} node={c} depth={depth + 1} doc={doc} folder={folder} />)}
    </Section>
  )
}
